import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import ChevronLeft from "@mui/icons-material/ChevronLeft";
import ChevronRight from "@mui/icons-material/ChevronRight";
import LogOut from "@mui/icons-material/Logout";

const Badge = ({ n, small }) =>
  n ? <span className={`nav-badge ${small ? "nav-badge-dot" : ""}`}>{n > 99 ? "99+" : n}</span> : null;

// The sidebar is a component of its own (it used to be created inside AppLayout, which made React rebuild it on every
// refresh and send the menu back to the top). `collapsed` shows icons only; hovering a group then opens a flyout.
export default function Sidebar({
  navTree, hasAccess, hiddenModules, collapsed, mobile, pathname, openGroups, toggleGroup, onToggleCollapse,
  onNavigate, user, onLogout, badges, logoSrc,
}) {
  const isCollapsed = collapsed && !mobile;
  const [flyout, setFlyout] = useState(null);       // { id, top }
  const closeTimer = useRef(null);
  const flyoutBox = useRef(null);

  // keep the pop-out on screen: slide it up when it would run past the bottom; a taller list scrolls inside it
  useLayoutEffect(() => {
    const el = flyoutBox.current;
    if (!flyout || !el) return;
    const room = window.innerHeight - 16;
    const height = Math.min(el.scrollHeight, room);
    const top = Math.max(8, Math.min(flyout.top, window.innerHeight - height - 8));
    el.style.top = `${top}px`;
    el.style.maxHeight = `${room}px`;
  }, [flyout]);

  useEffect(() => {
    if (!flyout) return undefined;
    const onKey = (e) => e.key === "Escape" && setFlyout(null);
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [flyout]);
  useEffect(() => { if (!isCollapsed) setFlyout(null); }, [isCollapsed]);

  const openFlyout = (id, el) => {
    clearTimeout(closeTimer.current);
    if (el) setFlyout({ id, top: el.getBoundingClientRect().top });
  };
  const closeFlyoutSoon = () => {
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setFlyout(null), 140);
  };

  const linkFor = (item, sub) => {
    if (!hasAccess(item.module) || hiddenModules.includes(item.module)) return null;
    const active = pathname.startsWith(item.path);
    const Icon = item.icon;
    return (
      <Link
        key={item.path}
        to={item.path}
        className={`nav-item ${sub ? "nav-sub-item" : ""} ${active ? "active" : ""}`}
        title={isCollapsed && !sub ? item.label : ""}
        aria-current={active ? "page" : undefined}
        onClick={() => { setFlyout(null); onNavigate(); }}
      >
        <Icon size={sub ? 16 : 18} className="nav-icon" />
        {(!isCollapsed || sub) && <span className="nav-label">{item.label}</span>}
        <Badge n={badges[item.module]} small={isCollapsed && !sub} />
      </Link>
    );
  };

  return (
    <div className="sidebar-inner">
      <div className="sidebar-logo">
        {!isCollapsed && (
          <div className="logo-brand">
            <img src={logoSrc} alt="CRM" className="logo-img" />
            <span className="logo-sub">Sales & Purchase</span>
          </div>
        )}
        <button className="collapse-btn" onClick={onToggleCollapse} title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}>
          {isCollapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </button>
      </div>

      <nav className="sidebar-nav" aria-label="Main">
        {navTree.map((node) => {
          if (node.type === "item") return linkFor(node.item, false);
          const visible = node.items.filter((it) => hasAccess(it.module) && !hiddenModules.includes(it.module));
          if (visible.length === 0) return null;
          const GIcon = node.icon;
          const open = !!openGroups[node.id] && !isCollapsed;
          const hasActive = visible.some((it) => pathname.startsWith(it.path));
          const groupCount = visible.reduce((sum, it) => sum + (badges[it.module] || 0), 0);
          return (
            <div
              key={`g${node.id}`}
              className="nav-group"
              onMouseEnter={(e) => isCollapsed && openFlyout(node.id, e.currentTarget)}
              onMouseLeave={() => isCollapsed && closeFlyoutSoon()}
            >
              <button
                type="button"
                className={`nav-item nav-group-btn ${hasActive ? "has-active" : ""}`}
                title={isCollapsed ? "" : undefined}
                aria-expanded={isCollapsed ? flyout?.id === node.id : open}
                aria-haspopup={isCollapsed ? "menu" : undefined}
                onFocus={(e) => isCollapsed && openFlyout(node.id, e.currentTarget)}
                onClick={(e) => (isCollapsed ? openFlyout(node.id, e.currentTarget) : toggleGroup(node.id))}
              >
                <GIcon size={18} className="nav-icon" />
                {!isCollapsed && <span className="nav-label" style={{ flex: 1, textAlign: "left" }}>{node.label}</span>}
                <Badge n={groupCount} small={isCollapsed} />
                {!isCollapsed && (
                  <ChevronRight size={16} className="nav-chevron" style={{ transform: open ? "rotate(90deg)" : "none", transition: "transform .15s" }} />
                )}
              </button>
              {!isCollapsed && (
                <div className={`nav-sub-wrap ${open ? "open" : ""}`}>
                  <div className="nav-sub-inner">
                    <div className="nav-sub">{visible.map((it) => linkFor(it, true))}</div>
                  </div>
                </div>
              )}
              {isCollapsed && flyout?.id === node.id && (
                <div className="nav-flyout" role="menu" ref={flyoutBox} style={{ top: Math.max(8, flyout.top) }}
                  onMouseEnter={() => clearTimeout(closeTimer.current)} onMouseLeave={closeFlyoutSoon}>
                  <div className="nav-flyout-title">{node.label}</div>
                  {visible.map((it) => linkFor(it, true))}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {user && (
        <div className="sidebar-footer">
          <div className="user-info">
            <div className="user-avatar">{(user.first_name?.[0] || user.email?.[0] || "U").toUpperCase()}</div>
            {!isCollapsed && (
              <div className="user-details">
                <span className="user-name">{user.first_name || user.email}</span>
                <span className="user-role">{user.is_superuser ? "Super Admin" : "User"}</span>
              </div>
            )}
          </div>
          <button className="logout-btn" onClick={onLogout} title="Logout" aria-label="Logout">
            <LogOut size={16} />
          </button>
        </div>
      )}
    </div>
  );
}
